import Link from "next/link";

// ─── Types ────────────────────────────────────────────────────────────────────

export type FeedEntry = {
  id: string;
  type: "session" | "checkin";
  userName: string;
  userInitial: string;
  badge: "seasonaire" | "member" | null;
  spotName: string;
  spotSlug: string;
  rating?: number;
  forecastAccuracy?: string | null;
  notes?: string | null;
  conditionsSnapshot?: {
    swellHeightM: number;
    swellPeriodS: number;
    windSpeedKmh: number;
  } | null;
  crowdLevel?: string | null;
  relativeTime: string;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Stars({ rating }: { rating: number }) {
  const filled = Math.round(rating);
  return (
    <span
      aria-label={`${filled} out of 5 stars`}
      className="text-[13px] text-amber-500"
    >
      {"★".repeat(filled)}
      {"☆".repeat(5 - filled)}
    </span>
  );
}

function BadgePill({ badge }: { badge: "seasonaire" | "member" }) {
  if (badge === "seasonaire") {
    return (
      <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700">
        Seasonaire
      </span>
    );
  }
  return (
    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600">
      Member
    </span>
  );
}

function crowdLevelLabel(level: string): string {
  switch (level) {
    case "empty":
      return "Empty";
    case "quiet":
      return "Quiet";
    case "moderate":
      return "Moderate";
    case "busy":
      return "Busy";
    case "packed":
      return "Packed";
    default:
      return level.charAt(0).toUpperCase() + level.slice(1);
  }
}

// ─── Feed entry card ──────────────────────────────────────────────────────────

function FeedCard({ entry }: { entry: FeedEntry }) {
  return (
    <div className="flex gap-3 py-4">
      {/* Avatar */}
      <div
        className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center shrink-0 text-[13px] font-semibold text-navy"
        aria-hidden
      >
        {entry.userInitial}
      </div>

      <div className="flex-1 min-w-0">
        {/* Line 1: name + badge + relative time */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-[13px] font-semibold text-navy">
            {entry.userName}
          </span>
          {entry.badge && <BadgePill badge={entry.badge} />}
          <span className="text-[11px] text-text-muted">
            {entry.relativeTime}
          </span>
        </div>

        {/* Line 2: action text */}
        <p className="text-[13px] text-text-body mt-0.5">
          {entry.type === "session" ? "Logged a session at " : "Checked in at "}
          <Link
            href={`/conditions/${entry.spotSlug}`}
            className="text-navy font-medium hover:underline"
          >
            {entry.spotName}
          </Link>
        </p>

        {/* Star rating (sessions only) */}
        {entry.type === "session" && entry.rating != null && (
          <div className="mt-1">
            <Stars rating={entry.rating} />
          </div>
        )}

        {/* Notes */}
        {entry.notes && (
          <p className="text-[13px] text-text-body italic line-clamp-2 mt-1">
            &ldquo;{entry.notes}&rdquo;
          </p>
        )}

        {/* Conditions snapshot (sessions) */}
        {entry.conditionsSnapshot && (
          <p className="text-[11px] text-text-muted mt-1">
            {entry.conditionsSnapshot.swellHeightM.toFixed(1)}m @{" "}
            {entry.conditionsSnapshot.swellPeriodS}s
            {" · "}
            Wind {entry.conditionsSnapshot.windSpeedKmh}km/h
          </p>
        )}

        {/* Crowd level (check-ins) */}
        {entry.type === "checkin" && entry.crowdLevel && (
          <p className="text-[11px] text-text-muted mt-1">
            Crowd: {crowdLevelLabel(entry.crowdLevel)}
          </p>
        )}

        {/* Forecast accuracy */}
        {entry.forecastAccuracy && (
          <p className="text-[11px] text-text-muted mt-0.5">
            Forecast:{" "}
            {entry.forecastAccuracy === "spot_on"
              ? "Spot on"
              : entry.forecastAccuracy === "close"
                ? "Close"
                : "Way off"}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Main feed component ──────────────────────────────────────────────────────

type Props = {
  entries: FeedEntry[];
};

export function CrewConditionsFeed({ entries }: Props) {
  if (entries.length === 0) {
    return (
      <div className="bg-white rounded-[16px] border border-border-light p-7 text-center">
        <p className="text-[14px] text-text-muted">
          No conditions activity yet. Be the first to log a session.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-7">
      <h3 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted mb-1">
        Recent activity
      </h3>
      <div>
        {entries.map((entry) => (
          <div
            key={`${entry.type}-${entry.id}`}
            className="border-b border-border-light last:border-b-0"
          >
            <FeedCard entry={entry} />
          </div>
        ))}
      </div>
    </div>
  );
}
